<template>
  <!-- 任务详情：任务信息 / 延期信息（可提交） / 任务节点（可更新进度） -->
  <div class="defaultViewStyle task-detail">
    <div v-if="task.id" class="td-root">
      <div class="td-header">
        <el-page-header @back="goBack" :content="(task.taskCode || '') + ' ' + (task.taskName || '')"/>
        <div class="td-tags">
          <el-tag size="small" :type="taskStatusTag(task.taskStatus)">{{ taskStatusLabel(task.taskStatus) || '—' }}</el-tag>
          <el-tag v-if="task.delayState && task.delayState !== '正常'" size="small" :type="task.delayState === '延期中' ? 'danger' : 'warning'">{{ task.delayState }}</el-tag>
        </div>
      </div>

      <!-- 任务信息 -->
      <div class="td-block">
        <div class="block-title">任务信息</div>
        <el-descriptions :column="3" border size="small">
          <el-descriptions-item label="任务编号">{{ task.taskCode || '—' }}</el-descriptions-item>
          <el-descriptions-item label="所属项目">{{ task.projectName || task.projectCode || '—' }}</el-descriptions-item>
          <el-descriptions-item label="所属批次">{{ task.sprintName || '—' }}</el-descriptions-item>
          <el-descriptions-item label="负责人">{{ task.ownerName || '—' }}</el-descriptions-item>
          <el-descriptions-item label="工时估算">{{ task.estimate != null ? task.estimate + ' 人天' : '—' }}</el-descriptions-item>
          <el-descriptions-item label="进度">{{ task.progress || 0 }}%</el-descriptions-item>
          <el-descriptions-item label="计划开始">{{ dateOnly(task.planStart) }}</el-descriptions-item>
          <el-descriptions-item label="计划完成">{{ dateOnly(task.planEnd) }}</el-descriptions-item>
          <el-descriptions-item label="实际完成">{{ dateOnly(task.actualEnd) }}</el-descriptions-item>
          <el-descriptions-item label="备注" :span="3">{{ task.remark || '—' }}</el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 延期信息 -->
      <div class="td-block">
        <div class="block-title">延期信息</div>
        <!-- 提交延期原因 -->
        <div class="delay-form">
          <div class="form-title">提交延期原因</div>
          <form-items :data="delayForm" :columns="3" label-width="2.4rem" icon-width="0" ref="delayFormRef">
            <form-item label="延期分类" :required="true" name="delayType" error-message="请选择延期分类">
              <el-select v-model="delayForm.delayType" placeholder="请选择延期分类" clearable filterable style="width: 100%">
                <el-option v-for="d in delayTypeOptions" :key="d.dictCode" :label="d.dictName" :value="d.dictCode"/>
              </el-select>
            </form-item>
            <form-item label="延期天数">{{delayForm.delayDays}}</form-item>
            <form-item label="原因说明" :show-block="true" align-items="flex-start">
              <yq-textarea placeholder="请说明延期原因（可 Ctrl+V 粘贴图片）" folder="projectTask" v-model:text="delayForm.delayReason" v-model:images="delayForm.delayReasonImages">
                <page-button type="write" el-type="primary" @click="submitDelay">提交延期信息</page-button>
              </yq-textarea>
            </form-item>
          </form-items>
        </div>
      </div>

      <!-- 任务节点 -->
      <div class="td-block">
        <div class="block-title">任务关键节点</div>
        <el-table :data="nodeForms" border size="small" :empty-text="'该任务暂无关键节点'">
          <el-table-column label="节点" min-width="160">
            <template v-slot="{row}">
              <b>{{ row.nodeName || '—' }}</b>
              <div class="muted" v-if="row.testerNames">{{ row.testerNames }}</div>
            </template>
          </el-table-column>
          <el-table-column label="开发天数" width="90" align="center">
            <template v-slot="{row}">{{ row.days != null ? row.days : '—' }}</template>
          </el-table-column>
          <el-table-column label="计划" width="200">
            <template v-slot="{row}">{{ dateOnly(row.planStart) }} ~ {{ dateOnly(row.planEnd) }}</template>
          </el-table-column>
          <el-table-column label="总工时(h)" width="90" align="center">
            <template v-slot="{row}">{{ row.hours || 0 }}</template>
          </el-table-column>
          <el-table-column label="完成进度(%)" width="160" align="center">
            <template v-slot="{row}">
              <el-input-number v-model="row.pct" :min="0" :max="100" :step="5" size="small" controls-position="right"
                               :disabled="task.taskStatus === TaskStatusCode.DONE" style="width: 120px"/>
            </template>
          </el-table-column>
          <el-table-column label="剩余工时(h)" width="110" align="center">
            <template v-slot="{row}">{{ calcRemain(row) }}</template>
          </el-table-column>
          <el-table-column label="标记完成" width="90" align="center">
            <template v-slot="{row}">
              <el-checkbox :model-value="row.done === 1" :disabled="task.taskStatus === TaskStatusCode.DONE" @change="setNodeDone(row, $event)"/>
            </template>
          </el-table-column>
        </el-table>
        <div class="form-foot">
          <page-button type="write" el-type="primary" :disabled="task.taskStatus === TaskStatusCode.DONE" @click="submitProgress">保存节点进度</page-button>
        </div>
      </div>

      <!-- 任务日志 -->
      <div class="td-block">
        <div class="block-title">任务日志</div>
        <el-timeline v-if="logList.length">
          <el-timeline-item v-for="log in logList" :key="log.id" :timestamp="dateTime(log.createdTime) + ' · ' + (log.createdName || '—')" placement="top">
            <div class="log-line">
              <el-tag size="small" type="info">{{ log.action }}</el-tag>
              <span class="log-detail">{{ log.detail }}</span>
            </div>
          </el-timeline-item>
        </el-timeline>
        <div v-else class="empty-block">暂无动态</div>
      </div>
    </div>
    <div v-else v-loading="true" class="td-loading"/>
  </div>
</template>

<script>
import {useRoute, useRouter} from "vue-router";
import Util from "@/util/util";
import ImageUpload from "@/components/h5/image/ImageUpload.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import commonApi from "@/api/commonApi";
import taskApi from "@/api/dz-project/project/taskApi";
import {TaskStatusCode, taskStatusLabel as taskStatusLabelOf, taskStatusTag as taskStatusTagOf} from "./projectStatus";
import YqTextarea from "@/components/h5/data/YqTextarea.vue";
export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "TaskDetail",
  components: {ImageUpload, FormItems, FormItem, YqTextarea},
  data() {
    return {
      taskId: "",
      task: {},
      nodeForms: [],
      logList: [],
      delayTypeOptions: [],
      delayForm: {delayType: "", delayReason: "", delayReasonImages: "", delayDays: null},
      TaskStatusCode,
    };
  },
  beforeRouteLeave(to, from, next) {
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    const id = this.route.query.id;
    if (!id) {
      return;
    }
    this.taskId = id;
    this.loadDelayTypes();
    this.loadDetail();
    this.loadLogs();
  },
  methods: {
    goBack() {
      this.router.back();
    },
    async loadLogs() {
      try {
        const list = await taskApi.queryTaskLogs(this.taskId);
        this.logList = list || [];
      } catch (e) {
        this.logList = [];
      }
    },
    async loadDelayTypes() {
      try {
        const list = await commonApi.queryUniqueDictionaryList("PROJECT_DELAY_TYPE");
        this.delayTypeOptions = list || [];
      } catch (e) {
        this.delayTypeOptions = [];
      }
    },
    async loadDetail() {
      const detail = await taskApi.detail(this.taskId);
      this.task = detail || {};
      this.nodeForms = (detail.nodes || []).map(n => ({
        nodeId: n.id,
        nodeName: n.nodeName,
        days: n.days,
        planStart: n.planStart || "",
        planEnd: n.planEnd || "",
        hours: n.hours != null ? Number(n.hours) : 0,
        pct: n.pct != null ? n.pct : 0,
        done: n.done || 0,
        testerNames: n.testerNames || "",
      }));
      // 延期表单回显
      this.delayForm = {
        delayType: detail.delayType || "",
        delayReason: detail.delayReason || "",
        delayReasonImages: detail.delayReasonImages || "",
        delayDays: detail.delayDays != null ? detail.delayDays : null,
      };
    },
    delayTypeLabel(code) {
      if (!code) {
        return "未填写";
      }
      const d = this.delayTypeOptions.find(x => x.dictCode === code);
      return d ? d.dictName : code;
    },
    calcRemain(row) {
      if (!row) {
        return 0;
      }
      const hours = Number(row.hours) || 0;
      const pct = Math.min(100, Math.max(0, Number(row.pct) || 0));
      return Math.round(hours * (100 - pct) / 100);
    },
    setNodeDone(row, checked) {
      if (checked) {
        row.pct = 100;
        row.done = 1;
      } else {
        row.done = 0;
      }
    },
    async submitDelay() {
      if (!this.$refs.delayFormRef.validCheck()) {
        return;
      }
      await taskApi.saveDelayInfo({
        taskId: this.taskId,
        delayType: this.delayForm.delayType,
        delayReason: this.delayForm.delayReason,
        delayReasonImages: this.delayForm.delayReasonImages || null,
        delayDays: this.delayForm.delayDays,
      });
      this.$message.success("延期信息已提交");
      this.loadDetail();
      this.loadLogs();
    },
    async submitProgress() {
      if (!this.nodeForms.length) {
        this.$message.warning("该任务暂无关键节点");
        return;
      }
      const updates = this.nodeForms.map(u => ({
        nodeId: u.nodeId,
        pct: u.pct,
        remain: this.calcRemain(u),
        done: u.done,
      }));
      const totalRemain = this.nodeForms.reduce((s, u) => s + this.calcRemain(u), 0);
      await taskApi.reportProgress({
        taskId: this.taskId,
        note: "",
        remain: totalRemain,
        updates: updates,
      });
      this.$message.success("节点进度已保存");
      this.loadDetail();
      this.loadLogs();
    },
    dateOnly(v) {
      return v ? String(v).slice(0, 10) : "—";
    },
    dateTime(v) {
      return v ? String(v).slice(0, 16) : "—";
    },
    taskStatusLabel(s) {
      return taskStatusLabelOf(s);
    },
    taskStatusTag(s) {
      return taskStatusTagOf(s);
    },
  },
};
</script>

<style lang="scss" scoped>
.task-detail {
  height: 100%;
  box-sizing: border-box;
  padding: 10px;
  .td-root {
    height: 100%;
    overflow: auto;
    background: #fff;
    border-radius: 4px;
    padding: 12px 16px;
    box-sizing: border-box;
  }
  .td-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--el-border-color-lighter);
    padding-bottom: 10px;
    .td-tags {
      display: flex;
      gap: 8px;
    }
  }
  .td-block {
    margin-top: 14px;
  }
  .block-title {
    font-weight: 700;
    margin: 12px 0 8px;
    color: var(--yq-font-color-base, #303133);
  }
  .muted {
    color: var(--yq-font-color-desc);
  }
  .delay-form {
    margin-top: 12px;
    border: 1px solid var(--el-border-color-lighter);
    border-radius: 6px;
    padding: 10px;
    .form-title {
      font-weight: 600;
      margin-bottom: 8px;
      color: #409eff;
    }
    .delay-upload {
      ::v-deep(.el-upload--picture-card) {
        width: 64px;
        height: 64px;
        line-height: 64px;
      }
      ::v-deep(.el-upload-list__item) {
        width: 64px;
        height: 64px;
      }
    }
  }
  .form-foot {
    text-align: right;
    margin-top: 8px;
  }
  .log-line {
    display: flex;
    align-items: center;
    gap: 8px;
    .log-detail {
      color: var(--yq-font-color-desc);
    }
  }
  .empty-block {
    color: #909399;
    padding: 12px 0;
  }
  .td-loading {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
